/**
 * Web Audio API Effects Manager
 * Handles all audio effects for DJ mixer decks
 */

export class AudioEffectsManager {
  constructor(audioElement) {
    this.audioElement = audioElement;
    this.audioContext = null;
    this.sourceNode = null;
    this.gainNode = null;
    
    // Effect nodes
    this.filterNode = null;
    this.delayNode = null;
    this.feedbackNode = null;
    this.convolver = null;
    
    // Effect states
    this.effectsEnabled = false;
  }

  /**
   * Initialize Web Audio API context and nodes
   */
  async initialize() {
    if (this.audioContext) return; // Already initialized

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();

      // Create source from audio element
      this.sourceNode = this.audioContext.createMediaElementSource(this.audioElement);
      
      // Create gain node for volume control
      this.gainNode = this.audioContext.createGain();
      
      // Create filter node (for high-pass/low-pass effects)
      this.filterNode = this.audioContext.createBiquadFilter();
      this.filterNode.type = 'lowpass';
      this.filterNode.frequency.value = 20000; // Neutral position
      this.filterNode.Q.value = 1;
      
      // Create delay node (for echo/delay effects)
      this.delayNode = this.audioContext.createDelay(5.0); // Max 5 seconds
      this.delayNode.delayTime.value = 0;
      
      // Create feedback node for echo
      this.feedbackNode = this.audioContext.createGain();
      this.feedbackNode.gain.value = 0;
      
      // Connect delay feedback loop
      this.delayNode.connect(this.feedbackNode);
      this.feedbackNode.connect(this.delayNode);
      
      // Create convolver for reverb
      this.convolver = this.audioContext.createConvolver();
      this.convolver.buffer = this.createReverbImpulse(2, 2, false);
      
      // Create wet/dry mix nodes
      this.dryGain = this.audioContext.createGain();
      this.wetGain = this.audioContext.createGain();
      this.dryGain.gain.value = 1;
      this.wetGain.gain.value = 0;
      
      // Connect the audio chain:
      // Source -> Filter -> Delay -> Convolver -> Dry/Wet Mix -> Gain -> Destination
      this.sourceNode.connect(this.filterNode);
      this.filterNode.connect(this.delayNode);
      this.filterNode.connect(this.dryGain);
      
      this.delayNode.connect(this.convolver);
      this.convolver.connect(this.wetGain);
      
      this.dryGain.connect(this.gainNode);
      this.wetGain.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      
      this.effectsEnabled = true;
      console.log('Audio effects initialized successfully');
    } catch (error) {
      console.error('Failed to initialize audio effects:', error);
      // Fallback: connect directly
      if (this.sourceNode && this.audioContext) {
        this.sourceNode.connect(this.audioContext.destination);
      }
    }
  }

  /**
   * Create impulse response for reverb effect
   */
  createReverbImpulse(duration, decay, reverse) {
    const sampleRate = this.audioContext.sampleRate;
    const length = sampleRate * duration;
    const impulse = this.audioContext.createBuffer(2, length, sampleRate);
    const left = impulse.getChannelData(0);
    const right = impulse.getChannelData(1);

    for (let i = 0; i < length; i++) {
      const n = reverse ? length - i : i;
      left[i] = (Math.random() * 2 - 1) * Math.pow(1 - n / length, decay);
      right[i] = (Math.random() * 2 - 1) * Math.pow(1 - n / length, decay);
    }
    return impulse;
  }

  /**
   * Set filter effect (0 = low-pass, 50 = neutral, 100 = high-pass)
   */
  setFilter(value) {
    if (!this.filterNode || !this.audioContext) return;

    try {
      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      if (value < 50) {
        // Low-pass filter
        this.filterNode.type = 'lowpass';
        // Map 0-50 to 20000-200 Hz
        const frequency = 20000 - ((50 - value) / 50) * 19800;
        this.filterNode.frequency.setValueAtTime(
          frequency,
          this.audioContext.currentTime
        );
      } else if (value > 50) {
        // High-pass filter
        this.filterNode.type = 'highpass';
        // Map 50-100 to 20-5000 Hz
        const frequency = 20 + ((value - 50) / 50) * 4980;
        this.filterNode.frequency.setValueAtTime(
          frequency,
          this.audioContext.currentTime
        );
      } else {
        // Neutral - no filtering
        this.filterNode.type = 'allpass';
      }
    } catch (error) {
      console.error('Error setting filter:', error);
    }
  }

  /**
   * Set echo/delay effect (0-100%)
   */
  setEcho(value) {
    if (!this.delayNode || !this.feedbackNode || !this.audioContext) return;

    try {
      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      if (value > 0) {
        // Map 0-100 to delay time 0-0.8 seconds
        const delayTime = (value / 100) * 0.8;
        this.delayNode.delayTime.setValueAtTime(
          delayTime,
          this.audioContext.currentTime
        );
        
        // Map feedback 0-100 to 0-0.7 (max feedback before it gets too intense)
        const feedback = (value / 100) * 0.7;
        this.feedbackNode.gain.setValueAtTime(
          feedback,
          this.audioContext.currentTime
        );
      } else {
        this.delayNode.delayTime.setValueAtTime(0, this.audioContext.currentTime);
        this.feedbackNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      }
    } catch (error) {
      console.error('Error setting echo:', error);
    }
  }

  /**
   * Set reverb effect (0-100%)
   */
  setReverb(value) {
    if (!this.wetGain || !this.dryGain || !this.audioContext) return;

    try {
      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      // Adjust wet/dry mix
      const wet = value / 100;
      const dry = 1 - (wet * 0.5); // Don't completely kill dry signal
      
      this.wetGain.gain.setValueAtTime(wet, this.audioContext.currentTime);
      this.dryGain.gain.setValueAtTime(dry, this.audioContext.currentTime);
    } catch (error) {
      console.error('Error setting reverb:', error);
    }
  }

  /**
   * Resume audio context (required after user interaction)
   */
  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      console.log('Audio context resumed');
    }
  }

  /**
   * Clean up resources
   */
  dispose() {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
